import xlsx from 'xlsx';
import { saveAs } from 'file-saver';
import qs from 'qs';

import { fetchApi, formatDate } from 'shared/functions';
import { orderTypes } from 'shared/constants/orderTypes';
import { serviceUrlPrefix } from 'utils';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { exportModes } from '../../shared/constants/exportModes';

const typeToPath = {
  [orderTypes.INSTRUMENT]: 'issues',
  [orderTypes.ENTITY]: 'issuers',
  [orderTypes.SIG_ENTITY]: 'issuers',
};

type ExportServiceParams = {
  items: string[];
  type: string;
  onError: (error: string) => void;
  onSuccess?: (message: string) => void;
  ratingType?: ratingTypes;
  dossierId?: string;
  selectAll?: boolean;
  mode?: string;
};

export const exportService = async ({
  items,
  type,
  onError,
  ratingType = ratingTypes.CREDIT_RATING,
  dossierId = undefined,
  selectAll = false,
  mode = exportModes.BASIC,
}: ExportServiceParams) => {
  const path = typeToPath[type];

  const urlPrefix = serviceUrlPrefix[ratingType];

  const resourceUrl =
    ratingType === ratingTypes.ESG_IMPACT
      ? `${urlPrefix}/companies/export`
      : `${urlPrefix}/storefront/${path}/export/`;

  const body = JSON.stringify(
    ratingType === ratingTypes.CREDIT_REVIEW && type === orderTypes.SIG_ENTITY
      ? {
          issuers: selectAll ? undefined : items,
          dossierId,
          selectAll,
        }
      : items,
  );

  const params = {
    method: 'POST',
    body,
    query: { exportMode: mode },
  };

  try {
    const response = await fetchApi(resourceUrl, params);

    if (!response.ok) {
      onError(response.statusText);
      return;
    }

    const blob = await response.blob();
    saveAs(blob, `${path}-${formatDate(new Date(), 'dd-MM-yyyy')}.xlsx`);
  } catch (e) {
    onError(e as string);
  }
};

export const asyncExportService = async ({
  items,
  onError,
  onSuccess,
  ratingType = ratingTypes.CREDIT_RATING,
  mode = exportModes.BASIC,
}: ExportServiceParams) => {
  const urlPrefix = serviceUrlPrefix[ratingType];

  const url = `${urlPrefix}/companies/export`;
  const body = JSON.stringify(items);

  const params = {
    method: 'POST',
    body,
    query: { exportMode: mode },
  };

  try {
    const response = await fetchApi(url, params);

    if (!response.ok) {
      onError(response.statusText);
      return;
    }

    if (onSuccess) {
      const respText = await response.text();
      onSuccess(respText);
    }
  } catch (e) {
    onError(e as string);
  }
};

export const exportToExcel = ({ fileName, workbookName, columns, data }: any) => {
  const worksheet = xlsx.utils.aoa_to_sheet([columns, ...data]);
  const workbook = xlsx.utils.book_new();

  xlsx.utils.book_append_sheet(workbook, worksheet, workbookName);
  xlsx.writeFile(workbook, `${fileName}-${formatDate(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

type ExportEntitiesParams = {
  items: string[];
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
  ratingType?: ratingTypes;
  portfolioId: string;
  selectedAll?: boolean;
  exportMode?: string;
};

export const exportEntities = async ({
  items,
  onError = () => {},
  onSuccess = () => {},
  ratingType,
  portfolioId,
  selectedAll = false,
  exportMode = exportModes.BASIC,
}: ExportEntitiesParams) => {
  const modules = {
    overview: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=overview`,
      body: { items: selectedAll ? [] : items },
    },
    [ratingTypes.CREDIT_RATING]: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=creditRating`,
      body: { items: selectedAll ? [] : items },
    },
    [ratingTypes.CREDIT_REVIEW]: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=creditReview`,
      body: { items: selectedAll ? [] : items },
    },
    [ratingTypes.ESG_IMPACT]: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=esg&exportMode=${exportMode}`,
      body: { items: selectedAll ? [] : items },
    },
    [ratingTypes.CLIMATE_STRESS_TEST]: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=climateStressTest&exportMode=${exportMode}`,
      body: { items: selectedAll ? [] : items },
    },
    [ratingTypes.FUNDS]: {
      url: `/user-profile/portfolios/excel-export/${portfolioId}?exportType=funds&exportMode=${exportMode}`,
      body: { items: selectedAll ? [] : items },
    },
  };

  const { url, body } = ratingType ? modules[ratingType] : modules.overview;

  try {
    const response = await fetchApi(url, {
      method: 'POST',
      body: JSON.stringify(body.items),
    });

    if (!response.ok) {
      onError(response.statusText);
      return;
    }

    const respText = await response.text();
    onSuccess(respText);
  } catch (e) {
    onError('Unable to export entities.');
  }
};

export const exportIndustries = async ({
  items,
  onError = () => {},
  onSuccess = () => {},
  portfolioId,
  selectedAll = false,
}: ExportEntitiesParams) => {
  const url = `/user-profile/portfolios/excel-export/${portfolioId}?exportType=instruments`;
  const body = { items: selectedAll ? [] : items };

  try {
    const response = await fetchApi(url, {
      method: 'POST',
      body: JSON.stringify(body.items),
    });

    if (!response.ok) {
      onError(response.statusText);
      return;
    }

    const respText = await response.text();
    onSuccess(respText);
  } catch (e) {
    onError('Unable to export instruments.');
  }
};

type ExportCoveredBondsParams = {
  items?: string[];
  countries?: string[];
  onError?: (error: string) => void;
  selectedAll?: boolean;
};

export const exportCoveredBonds = async ({
  items,
  countries,
  onError,
  selectedAll,
}: ExportCoveredBondsParams) => {
  const countryParam = countries ? `?filters=${JSON.stringify({ countries })}` : '';
  const url = `/storefront/issues/coveredbonds/export${qs.stringify(countryParam)}`;

  const body = items && !selectedAll ? JSON.stringify(items) : undefined;

  const params = {
    method: 'POST',
    body,
  };

  try {
    const response = await fetchApi(url, params);

    if (!response.ok) {
      if (onError) {
        onError(response.statusText);
      }
      return;
    }

    const blob = await response.blob();
    saveAs(blob, `covered-bonds-${formatDate(new Date(), 'dd-MM-yyyy')}.xlsx`);
  } catch (e) {
    if (onError) {
      onError(e as string);
    }
  }
};
